import { CardMediaImage } from "modules/common/components/CardMediaImage";
import React, { ViewTransition } from "react";
import { FinishEnum, GameCard, NONFOIL_FINISH } from "modules/cards/model/card";
import Link from "next/link";
import { CardMarketIcon } from "modules/common/components/Icons/CardMarketIcon";
import { CardPrimaryPrice } from "modules/cards/components/CardPrimaryPrice";
import { DeckCardData } from "modules/decks/model/deck";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { SizingOptions } from "modules/common/components/CardPreviewLoading";
import { CardPreviewSizing } from "utils/styles";
import { CardActionsButton } from "modules/cards/actions/components/CardActionsButton";
import { useCardModal } from "modules/cards/stores/useCardsStore";
import { usePricingRequest } from "modules/common/hooks/usePricingRequest";
import {
  marketplacePurchaseUrl,
  pricingSourceForMarket,
} from "modules/common/pricing";
import {
  finishSpec,
  primaryAvailableFinish,
} from "modules/cards/model/finishBehavior";
import { CardPriceMarketEnum } from "modules/cards/model/card-price";
import { ShoppingCart } from "lucide-react";
import { getCardPresentationAdapter } from "modules/common/gameRegistry";

const MARKET_ICONS: Partial<
  Record<CardPriceMarketEnum, React.ComponentType<{ fontSize?: number }>>
> = {
  [CardPriceMarketEnum.Cardmarket]: CardMarketIcon,
};

interface Props {
  card: GameCard | DeckCardData;
  flipped: boolean;
  setFlipped: (flipped: boolean) => void;
  finish?: FinishEnum;
  sizing?: SizingOptions;
  isPriority?: boolean;
  /** Pairs this image with same-named card images across navigations. */
  viewTransitionName?: string;
}

export const CardImageMarketIcons = ({
  flipped,
  setFlipped,
  card,
  finish,
  sizing = CardPreviewSizing,
  isPriority,
  viewTransitionName,
}: Props) => {
  const {
    modal: { open },
  } = useCardModal();
  const { pricing } = usePricingRequest();

  const displayFinish =
    finish ?? primaryAvailableFinish(card.finishes ?? []) ?? NONFOIL_FINISH;
  const foil = finishSpec(displayFinish).foilVisual;
  const market = pricing?.market;
  const marketSource = market ? pricingSourceForMarket(market) : undefined;
  const marketName = marketSource?.name;
  const marketUrl = market
    ? marketplacePurchaseUrl(card.game, card.printing, market, displayFinish)
    : undefined;
  const MarketIcon = market ? MARKET_ICONS[market] : undefined;

  const media = (
    <CardMediaImage
      flipped={flipped}
      setFlipped={setFlipped}
      card={card}
      isFoil={foil}
      sizing={sizing}
      priority={isPriority || !open}
      fetchPriority={isPriority ? "high" : "low"}
    />
  );

  return (
    <div className="flex w-full flex-col items-center justify-center gap-2">
      {viewTransitionName ? (
        <ViewTransition
          name={viewTransitionName}
          share="vt-hero"
          default="none"
        >
          {media}
        </ViewTransition>
      ) : (
        media
      )}

      <div className="flex flex-row items-center justify-between gap-2">
        {marketUrl && marketName && (
          <Link
            href={marketUrl}
            target="_blank"
            aria-label={`Buy ${card.name} on ${marketName}`}
            className="btn btn-outline btn-sm btn-primary flex items-center gap-2"
          >
            {MarketIcon ? (
              <MarketIcon fontSize={ICON_SIZE} />
            ) : (
              <ShoppingCart aria-hidden size={ICON_SIZE} />
            )}
            <span className="hidden md:inline-flex">Buy @ {marketName}</span>
            <CardPrimaryPrice card={card} finish={displayFinish} />
          </Link>
        )}
        {"features" in card &&
          getCardPresentationAdapter(card.game).supportsCardActions &&
          !open && <CardActionsButton card={card} variant="button" />}
      </div>
    </div>
  );
};
